import React, { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, Loader2, XCircle, User2, RefreshCw, Calendar } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useTasks } from '../../../../context/TasksContext';
import { useCompany } from '../../../../context/CompanyContext';
import { NON_TERMINAL_RECURRING_STATUSES } from '../../../../utils/buildForecast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';

/**
 * AccountCloseDialog — S4 V1 lifecycle.
 *
 * Customer-level close action with three pieces of context an operator
 * needs at decision time:
 *   1. Who they're closing (customer name + active recurring program)
 *   2. The structured close reason (8-option taxonomy)
 *   3. What to do with future scheduled visits (explicit binary choice)
 *
 * Parent (AccountStatusMenu) handles the API call + toast; this component
 * gathers the summary data, renders the form, and forwards the operator's
 * choices via onConfirm({ reason, reasonNote, cancelFutureVisits }).
 */

const REASON_OPTIONS = [
  { value: 'customer_cancelled', label: 'Customer cancelled' },
  { value: 'moved',              label: 'Moved' },
  { value: 'outside_service_area', label: 'Outside service area' },
  { value: 'price',              label: 'Price' },
  { value: 'switched_providers', label: 'Switched providers' },
  { value: 'non_payment',        label: 'Non-payment' },
  { value: 'deceased',           label: 'Deceased' },
  { value: 'other',              label: 'Other' },
];

// Render the cadence string operators recognize ("Every 21 Days").
function formatCadence(recurrence) {
  if (!recurrence || !recurrence.interval) return null;
  const interval = recurrence.interval;
  const unit = (recurrence.unit || 'days').toLowerCase();
  // Singular/plural unit display.
  const unitLabel = interval === 1
    ? unit.replace(/s$/, '')
    : unit.endsWith('s') ? unit : `${unit}s`;
  return `Every ${interval} ${unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1)}`;
}

const AccountCloseDialog = ({
  isOpen,
  lead,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const { companyId } = useCompany();
  const { tasks } = useTasks();

  const [reason, setReason] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [futureVisitsChoice, setFutureVisitsChoice] = useState(null); // null | true (cancel) | false (keep)
  const [programName, setProgramName] = useState(null);

  // Reset form whenever the dialog re-opens.
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setReasonNote('');
      setFutureVisitsChoice(null);
    }
  }, [isOpen]);

  // Load the linked program template (one-shot) when the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    const templateId = lead?.servicePlanTemplateId;
    if (!templateId || !companyId) {
      setProgramName(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, 'companies', companyId, 'servicePlanTemplates', templateId);
        const snap = await getDoc(ref);
        if (!cancelled && snap.exists()) {
          const data = snap.data() || {};
          setProgramName(data.name || null);
        }
      } catch {
        if (!cancelled) setProgramName(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, companyId, lead?.servicePlanTemplateId]);

  // Future-visit count from in-memory tasks list (mirrors S2 logic).
  const futureVisitCount = useMemo(() => {
    if (!lead?.id || !Array.isArray(tasks)) return 0;
    const nowIso = new Date().toISOString();
    return tasks.filter((t) => {
      if (t.leadId !== lead.id) return false;
      if (t.type !== 'job') return false;
      if (!NON_TERMINAL_RECURRING_STATUSES.has(t.status)) return false;
      if (typeof t.scheduledStart !== 'string' || t.scheduledStart < nowIso) return false;
      return (
        t.isRecurring === true
        || t.recurringServiceId === lead.id
        || t.seriesIndex != null
        || t.visitNumber != null
      );
    }).length;
  }, [lead?.id, tasks]);

  // Customer display name.
  const customerName = (() => {
    if (!lead) return 'this customer';
    if (lead.name && typeof lead.name === 'string') return lead.name.trim();
    const first = lead.firstName || '';
    const last  = lead.lastName  || '';
    const combined = `${first} ${last}`.trim();
    return combined || lead.email || lead.phone || 'this customer';
  })();

  // Active program label combines program name + cadence ("Mosquito Control (Every 21 Days)").
  const activeProgramLabel = useMemo(() => {
    const isActive = lead?.recurrence?.active === true;
    if (!isActive) return null;
    const cadence = formatCadence(lead?.recurrence);
    if (programName && cadence) return `${programName} (${cadence})`;
    if (programName) return programName;
    if (cadence) return cadence;
    if (lead?.recurrence?.planLabel) return lead.recurrence.planLabel;
    return 'Recurring program (no template linked)';
  }, [lead, programName]);

  const hasVisits = futureVisitCount > 0;
  const canConfirm =
    reason !== ''
    && (!hasVisits || futureVisitsChoice !== null)
    && !submitting;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      reason,
      reasonNote: reasonNote.trim() || null,
      // No future visits → endpoint accepts whatever; we pass false so the
      // backend cascade is a no-op.
      cancelFutureVisits: hasVisits ? futureVisitsChoice === true : false,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !submitting && onCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="account-close-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Close Account?
          </DialogTitle>
          <DialogDescription className="pt-1">
            Closing ends the relationship for this customer. The customer profile,
            invoices, and history remain.
          </DialogDescription>
        </DialogHeader>

        {/* Customer summary block — operator clarity */}
        <div
          className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-1.5"
          data-testid="account-close-summary"
        >
          <div className="flex items-center gap-2 text-sm">
            <User2 className="w-4 h-4 text-gray-500 shrink-0" />
            <span className="text-gray-600">Customer:</span>
            <span className="font-medium text-gray-900 truncate" data-testid="account-close-customer">
              {customerName}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <RefreshCw className="w-4 h-4 text-gray-500 shrink-0" />
            <span className="text-gray-600">Active program:</span>
            <span className="font-medium text-gray-900 truncate" data-testid="account-close-program">
              {activeProgramLabel || 'None'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-gray-500 shrink-0" />
            <span className="text-gray-600">Future scheduled visits:</span>
            <span className="font-medium text-gray-900" data-testid="account-close-future-count">
              {futureVisitCount}
            </span>
          </div>
        </div>

        {/* Reason picker */}
        <div className="space-y-1.5">
          <label htmlFor="account-close-reason" className="text-sm font-medium text-gray-700">
            Reason for closing <span className="text-red-600">*</span>
          </label>
          <select
            id="account-close-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            data-testid="account-close-reason-select"
          >
            <option value="">Select a reason…</option>
            {REASON_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <textarea
            value={reasonNote}
            onChange={(e) => setReasonNote(e.target.value)}
            placeholder="Optional notes (recommended for 'Other')"
            disabled={submitting}
            rows={2}
            maxLength={500}
            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 resize-none"
            data-testid="account-close-reason-note"
          />
        </div>

        {/* Future-visits choice — only when there are any */}
        {hasVisits && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Future scheduled visits ({futureVisitCount}) <span className="text-red-600">*</span>
            </div>
            <label
              className={`flex gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                futureVisitsChoice === true
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              data-testid="account-close-choice-cancel"
            >
              <input
                type="radio"
                name="account-close-visit-choice"
                checked={futureVisitsChoice === true}
                onChange={() => setFutureVisitsChoice(true)}
                disabled={submitting}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium text-gray-900">Cancel future visits</div>
                <div className="text-xs text-gray-600 mt-0.5 leading-snug">
                  The {futureVisitCount} scheduled visit{futureVisitCount === 1 ? '' : 's'}{' '}
                  will be cancelled (cancelReason=&apos;account_closed&apos;).
                </div>
              </div>
            </label>
            <label
              className={`flex gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                futureVisitsChoice === false
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              data-testid="account-close-choice-keep"
            >
              <input
                type="radio"
                name="account-close-visit-choice"
                checked={futureVisitsChoice === false}
                onChange={() => setFutureVisitsChoice(false)}
                disabled={submitting}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium text-gray-900">Keep future visits</div>
                <div className="text-xs text-gray-600 mt-0.5 leading-snug">
                  The visits stay on the calendar. The account closes immediately.
                </div>
              </div>
            </label>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
            data-testid="account-close-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="account-close-confirm-btn"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            <XCircle className="w-4 h-4 mr-1.5" />
            Close Account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AccountCloseDialog;
